/**
 * Invitation acceptance page for Aegix One.
 *
 * When a guardian or student clicks an invitation link (/invite/:token),
 * this page validates the token, shows what students will be linked,
 * and handles the accept flow.
 *
 * If not authenticated, shows login/register options with invitation context.
 * If authenticated, accepts immediately and redirects to dashboard.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Text,
  Image,
  VStack,
  HStack,
  Spinner,
  Center,
  Link,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@themes/aegix';
import { useIsAuthenticated } from '@aegix/auth-identity';
import { useColorMode } from '@/components/ui/color-mode';
import logoLight from '@/assets/aegix-one-logo-light.svg';
import logoDark from '@/assets/aegix-one-logo-dark.svg';
import { client, unwrap } from '@/lib/http-client';

interface InviteStudent {
  public_id: string;
  first_name: string;
  last_name: string;
  grade: string;
  homeroom: string;
}

interface InviteDetail {
  public_id: string;
  school_name: string;
  recipient_name: string;
  role: string;
  students: InviteStudent[];
  status: string;
  is_redeemable: boolean;
  expires_at: string;
}

export default function AcceptInvite() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();
  const { colorMode } = useColorMode();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<InviteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  // Fetch invite details
  useEffect(() => {
    if (!token) return;

    void (async () => {
      try {
        const data = unwrap(
          await client.GET('/api/v2/one/invite/{token}/', {
            params: { path: { token } as never },
          }),
        ) as unknown as InviteDetail;
        setInvite(data);
        setLoading(false);
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t('acceptInvite.invalidLink'),
        );
        setLoading(false);
      }
    })();
  }, [token, t]);

  // Auto-accept if authenticated and invite is redeemable
  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    setError('');

    try {
      unwrap(
        await client.POST('/api/v2/one/invite/{token}/accept/', {
          params: { path: { token } as never },
          body: {} as never,
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['my-students'] });
      setAccepted(true);
      // Short delay then redirect
      setTimeout(() => navigate('/', { replace: true }), 1500);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('acceptInvite.failedAccept'),
      );
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Center h="100vh" bg="surface.primary">
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <Flex
      height="100vh"
      alignItems="center"
      justifyContent="center"
      bg="surface.primary"
    >
      <Box
        maxW="440px"
        w="100%"
        p={8}
        borderWidth="1px"
        borderColor="border.primary"
        borderRadius="lg"
        bg="surface.primary"
        boxShadow="0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)"
      >
        <VStack gap={6}>
          <Image
            src={colorMode === 'dark' ? logoDark : logoLight}
            alt="Aegix One"
            w="200px"
          />

          {/* Error state */}
          {error && !invite && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="red.500">
                {t('acceptInvite.invalidTitle')}
              </Text>
              <Text fontSize="sm" color="text.secondary" textAlign="center">
                {error}
              </Text>
              <Link asChild color="blue.500">
                <RouterLink to="/login">{t('acceptInvite.goSignIn')}</RouterLink>
              </Link>
            </>
          )}

          {/* Accepted state */}
          {accepted && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="green.600">
                {t('acceptInvite.acceptedTitle')}
              </Text>
              <Text fontSize="sm" color="text.secondary" textAlign="center">
                {t('acceptInvite.acceptedDesc')}
              </Text>
            </>
          )}

          {/* Invite details */}
          {invite && !accepted && (
            <>
              <VStack gap={2}>
                <Text
                  fontSize="lg"
                  fontWeight="semibold"
                  color="text.primary"
                >
                  {t('acceptInvite.invitedTitle')}
                </Text>
                <Text
                  fontSize="sm"
                  color="text.secondary"
                  textAlign="center"
                >
                  {invite.role === 'self'
                    ? t('acceptInvite.invitedDescSelf', { school: invite.school_name })
                    : t('acceptInvite.invitedDescStudents', { school: invite.school_name, count: invite.students.length })}
                </Text>
              </VStack>

              {/* Student list */}
              {invite.students.length > 0 && (
                <Box
                  w="100%"
                  borderWidth="1px"
                  borderColor="border.primary"
                  borderRadius="md"
                  p={3}
                >
                  <Text fontSize="xs" color="text.tertiary" mb={2}>
                    {invite.role === 'self' ? t('acceptInvite.yourRecord') : t('students.tabStudents')}
                  </Text>
                  <VStack gap={2} align="stretch">
                    {invite.students.map((student) => (
                      <HStack key={student.public_id} gap={3}>
                        <Box>
                          <Text fontSize="sm" fontWeight="medium">
                            {student.first_name} {student.last_name}
                          </Text>
                          {student.grade && (
                            <Text fontSize="xs" color="text.secondary">
                              {t('acceptInvite.gradeLabel', { grade: student.grade })}
                              {student.homeroom
                                ? ` - ${student.homeroom}`
                                : ''}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* Not redeemable */}
              {!invite.is_redeemable && (
                <Text fontSize="sm" color="red.500" textAlign="center">
                  {invite.status === 'expired'
                    ? t('acceptInvite.expiredNotice')
                    : t('acceptInvite.invalidNotice', { status: invite.status })}
                </Text>
              )}

              {/* Action buttons */}
              {invite.is_redeemable && (
                <>
                  {isAuthenticated ? (
                    <VStack gap={3} w="100%">
                      <Button
                        visual="primary"
                        onClick={handleAccept}
                        disabled={accepting}
                        style={{ width: '100%' }}
                      >
                        {accepting ? t('acceptInvite.linking') : t('acceptInvite.acceptBtn')}
                      </Button>
                      {error && (
                        <Text color="red.500" fontSize="sm">
                          {error}
                        </Text>
                      )}
                    </VStack>
                  ) : (
                    <VStack gap={3} w="100%">
                      <Text
                        fontSize="sm"
                        color="text.secondary"
                        textAlign="center"
                      >
                        {t('acceptInvite.authNotice')}
                      </Text>
                      <Link asChild style={{ width: '100%' }}>
                        <RouterLink
                          to={`/login?invite=${token}`}
                          style={{ width: '100%' }}
                        >
                          <Button
                            visual="primary"
                            style={{ width: '100%' }}
                          >
                            {t('login.signIn')}
                          </Button>
                        </RouterLink>
                      </Link>
                      <Link asChild style={{ width: '100%' }}>
                        <RouterLink
                          to={`/register?invite=${token}`}
                          style={{ width: '100%' }}
                        >
                          <Button
                            visual="secondary"
                            style={{ width: '100%' }}
                          >
                            {t('login.createAccount')}
                          </Button>
                        </RouterLink>
                      </Link>
                    </VStack>
                  )}
                </>
              )}
            </>
          )}
        </VStack>
      </Box>
    </Flex>
  );
}
